/**
 * RegelwerkPage — Administration › Regelwerk (P2 Placement Slice 3).
 *
 * Two tabs over the per-tenant Regelwerk:
 *   «Soll-Deckungskatalog»  — the Soll-Deckung rules + Basissparten (target_coverage_catalog)
 *   «Attribut-Schema»       — the atomic field schema (branch_attribute_schema)
 *
 * Read-gated by regelwerk.read (the nav + AdminSection); the write controls are additionally gated
 * on regelwerk.manage (the api is the hard line). Mirrors the RechnungskontrollePage config-GET/PUT
 * + the ProzessErinnerungenPage save pattern.
 */

import { useState } from 'react';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { SollDeckungTab } from './regelwerk/SollDeckungTab';
import { AttributSchemaTab } from './regelwerk/AttributSchemaTab';
import { OfferScoreGewichteCard } from './OfferScoreGewichteCard';

const meta = ADMIN_PAGE_META.regelwerk;
type RegelwerkTab = 'soll' | 'schema';

export function RegelwerkPage() {
  const { can } = useEffectivePermissions();
  const canManage = can('regelwerk.manage');
  const [tab, setTab] = useState<RegelwerkTab>('soll');

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      <div role="tablist" aria-label="Regelwerk-Bereiche" className="mb-4 flex gap-2 border-b border-border">
        <TabButton active={tab === 'soll'} onClick={() => setTab('soll')} testId="regelwerk-tab-soll">
          Soll-Deckungskatalog
        </TabButton>
        <TabButton active={tab === 'schema'} onClick={() => setTab('schema')} testId="regelwerk-tab-schema">
          Attribut-Schema
        </TabButton>
      </div>

      {tab === 'soll' ? <SollDeckungTab canManage={canManage} /> : <AttributSchemaTab canManage={canManage} />}

      <OfferScoreGewichteCard canManage={canManage} />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={
        'relative -mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ' +
        (active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}
